import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { LinearProgress, styled, Typography, Card, CardHeader, CardContent } from "@mui/material";

import useDataSource from "../../../../hooks/useDataSource";
import * as DataUtilities from "../../../../utilities/DataUtilities";
import ClearFilterBtn from "../../../application/ClearFilterBtn";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

const PREFIX = 'Writeup';

const classes = {
	root: `${PREFIX}-root`,
	linearProgress: `${PREFIX}-linearProgress`,
	cardHeader: `${PREFIX}-cardHeader`,
	bulletIcon: `${PREFIX}-bulletIcon`
};

const Root = styled('div')((
	{
		theme
	}
) => ({
	[`&.${classes.root}`]: {
		height: 'calc(100% - 1px)',
		backgroundColor: theme.palette.background.default
	},

	[`& .${classes.linearProgress}`]: {
		height: '1px'
	},

	[`& .${classes.cardHeader}`]: {
		padding: '8px 8px 0px 8px',
		alignItems: 'start',
		textAlign: 'left',
		textTransform: 'none'
	},

	[`& .${classes.bulletIcon}`]: {
		width: '8px',
		height: '8px',
		marginTop: '4px',
		marginLeft: '4px',
		marginRight: '14px'
	}
}));

// export default function Writeup({title, subtitle, card, dataSourceName, showFetching, writeupField, grouping, condition}) {
export default function Writeup({id, configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();
	const { t } = useTranslation();

	// App State
	const persist = useSelector(state => state.persist);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	const [writeup, setWriteup] = useState(null);
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

	const dataSourceName = compConfig.dataSource?.name !== undefined ? compConfig.dataSource?.name : '';
	const field = compConfig.dataSource?.fields?.valueField !== undefined ? compConfig.dataSource?.fields?.valueField : '';
	const grouping = compConfig.dataSource?.fields?.grouping !== undefined ? compConfig.dataSource?.fields?.grouping : null;
	const limitDataSet = compConfig.dataSource?.fields?.limitDataSet !== undefined ? compConfig.dataSource?.fields?.limitDataSet : null;
	const limitDataSetDefaultVal = compConfig.dataSource?.fields?.limitDataSetDefaultVal !== undefined ? compConfig.dataSource?.fields?.limitDataSetDefaultVal : null;
	const styles = compConfig?.element?.styles;
	const variant = compConfig.element?.variant !== undefined ? compConfig.element?.variant : 'caption';
	const condition = compConfig.dataSource?.condition !== undefined ? compConfig.dataSource?.condition : null;

	const dataSource = useDataSource(dataSourceName);

	// Loading
	useEffect(() => {
		if (dataSource.fetching === true) {
			if (isFunction(onLoading)) {
				onLoading();
			}
		}
	}, [dataSource.fetching]);

	// Loaded
	useEffect(() => {
		if (dataSource.fetching === false && dataSource.fetched === true) {
			if (isFunction(onLoaded)) {
				onLoaded();
			}
		}
	}, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// Data
	useEffect(() => {
		if (dataSource.data && dataSource.fetched) {
			let uniqueWriteups = [];

			if (dataSource.data.length > 1) {
				switch(limitDataSet) {
					case "zero":
						uniqueWriteups.push(limitDataSetDefaultVal);
						break;
					case "one":
						if (condition && (dataSource.data[0][field] !== null)) {
							const val = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, condition.conditionValue, dataSource.data[0], field);
							if (val) {  // Don't push NULL data
								uniqueWriteups.push(val);
							}
						} else {
							if (dataSource.data[0][field] !== null) {
								uniqueWriteups.push(dataSource.data[0][field]);
							}
						}
						break;
					case "many":
					default:
						for (let i = 0; i < dataSource.data.length; i++) {
							if (condition && (dataSource.data[i][field] !== null)) {
								const val = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, condition.conditionValue, dataSource.data[i], field);
								if (val) {  // Don't push NULL data
									uniqueWriteups.push(val);
								}
							} else {
								if (dataSource.data[i][field] !== null) {
									uniqueWriteups.push(dataSource.data[i][field]);
								}
							}
						}
						break;
				}
			} else if (dataSource.data.length === 1) {
				if (condition && (dataSource.data[0][field] !== null)) {
					const val = DataUtilities.filterDataSet(condition.conditionType, condition.conditionOperator, condition.conditionField, condition.conditionValue, dataSource.data[0], field);
					if (val) {  // Don't push NULL data
						uniqueWriteups.push(val);
					}
				} else {
					if (dataSource.data[0][field] !== null) {
						uniqueWriteups.push(dataSource.data[0][field]);
					}
				}
			}

			const formattedWriteups = uniqueWriteups.map(item => setLayout(item));
			setWriteup(formattedWriteups);
		} else {
			setWriteup(""); // TODO: Can we set this differently, i.e. from config?
		}
	}, [dataSource.data, persist.themeLight]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Writeup Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'Writeup'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {
			...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflowY: 'auto'
			,padding: '8px'
		});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = newCardHeaderStyle?.height !== undefined ? newCardHeaderStyle.height : '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
			newCardContentStyle.fontSize = newCardContentStyle.fontSize !== undefined  ? newCardContentStyle.fontSize : "default";
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = newCardHeaderStyle.height !== undefined  ? newCardHeaderStyle.height : '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

	const setLayout = (writeup) => {
		return (
			<React.Fragment>
				{getGroupingIcon(grouping)}
				<Typography variant={variant} dangerouslySetInnerHTML={{ __html: writeup }}/>
			</React.Fragment>
		);
	}

	const getGroupingIcon = (grouping) => {
		let icon;

		if (grouping) {
			switch (grouping.toString().toLowerCase()) {
				case "bullet":
					icon = <FiberManualRecordIcon className={classes.bulletIcon}/>;
					break;
				case "":
					icon = "";
					break;
				case null:
					icon = "";
					break;
				default:
					icon = "";
			}
		} else {
			icon = "";
		}

		return icon;
	};

	function isFunction(functionToCheck) {
		return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
	}

	return (
		<Root className={classes.root}>
			{showProgress && dataSource.fetching &&
				<LinearProgress className={classes.linearProgress}/>
			}
			{showProgress && !dataSource.fetching &&
				<div className={classes.linearProgress}></div>
			}
			<Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
				<CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
							titleTypographyProps={{color: cardHeaderStyle.color ? cardHeaderStyle.color : "textPrimary", variant: "subtitle2", fontSize: cardHeaderStyle.fontSize ? cardHeaderStyle.fontSize : "default"}}
							title={t(title)}
							subheaderTypographyProps={{color: "textSecondary", variant: "body2",}}
							subheader={t(subtitle)}></CardHeader>
				<CardContent style={cardContentStyle}>
					{/*<Typography variant="caption" display="block" dangerouslySetInnerHTML={{ __html: writeup }}></Typography>*/}
					{writeup &&
						writeup.map((p, i) => {
							return (
								<div key={i}>
									{p}
								</div>
							);
						})
					}
				</CardContent>
			</Card>

		</Root>
	);
}
